import React, { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { LogOut, Library, Upload, ListChecks, BookOpen, Bell, ShieldCheck } from "lucide-react";
import { LogoMark } from "../components/Logo";
import { makeClient, errMsg, useMeta, TOKEN_KEY } from "../components/red/api";
import { Btn, Field, Input, Spinner } from "../components/red/ui";
import { ProvLoc } from "../components/red/ProvLoc";
import { Biblioteca } from "../components/red/Biblioteca";
import { EnvioForm } from "../components/red/EnvioForm";
import { MisEnvios } from "../components/red/MisEnvios";
import { EnviosList } from "../components/red/EnviosList";
import { ForgotForm, ResetForm } from "../components/red/PasswordForms";

const AuthScreen = ({ meta, onLogin }) => {
  const [params, setParams] = useSearchParams();
  const resetToken = params.get("reset");
  const [mode, setMode] = useState(resetToken ? "reset" : "login");
  const [f, setF] = useState({ nombre: "", email: "", password: "", provincia: "", localidad: "", telefono: "" });
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState("");
  const client = useMemo(() => makeClient({}), []);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setMsg("");
    try {
      if (mode === "login") {
        const { data } = await client.post("/auth/login", { email: f.email, password: f.password });
        onLogin(data);
      } else {
        await client.post("/auth/register", f);
        setMode("login");
        setMsg("¡Listo! Tu cuenta quedó pendiente de aprobación. Te avisamos por email cuando esté activa.");
      }
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0A192F] flex items-center justify-center p-4" data-testid="portal-auth">
      <div className="w-full max-w-lg bg-white">
        <div className="p-6 border-b flex items-center gap-4">
          <LogoMark className="h-10" />
          <div>
            <div className="font-headings uppercase tracking-[0.2em] text-[11px] text-[#D97706]">Red Nacional de Contenidos</div>
            <h1 className="font-headings uppercase text-2xl text-[#0A192F]">Portal de colaboradores</h1>
          </div>
        </div>
        {mode === "forgot" || mode === "reset" ? (
          mode === "forgot" ? (
            <ForgotForm client={client} onBack={() => setMode("login")} />
          ) : (
            <ResetForm client={client} token={resetToken} onDone={(m) => { setParams({}); setMode("login"); setMsg(m); }} />
          )
        ) : (
        <>
        <div className="flex border-b">
          {[["login", "Ingresar"], ["register", "Quiero colaborar"]].map(([m, l]) => (
            <button key={m} onClick={() => setMode(m)} className={`flex-1 py-3 font-headings uppercase text-sm tracking-wide border-b-2 ${mode === m ? "border-[#F5B400] text-[#0A192F]" : "border-transparent text-gray-400"}`} data-testid={`portal-tab-${m}`}>{l}</button>
          ))}
        </div>
        <form onSubmit={submit} className="p-6 grid sm:grid-cols-2 gap-4">
          {msg && <p className="sm:col-span-2 text-sm bg-emerald-50 border border-emerald-300 text-emerald-800 p-3" data-testid="portal-registro-ok">{msg}</p>}
          {mode === "register" && <Field label="Nombre y apellido *" className="sm:col-span-2"><Input required minLength={2} value={f.nombre} onChange={(e) => setF({ ...f, nombre: e.target.value })} data-testid="portal-nombre" /></Field>}
          <Field label="Email *" className="sm:col-span-2"><Input required type="email" autoComplete="email" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} data-testid="portal-email" /></Field>
          <Field label={mode === "register" ? "Contraseña * (mín. 8)" : "Contraseña *"} className="sm:col-span-2"><Input required type="password" minLength={mode === "register" ? 8 : 1} autoComplete={mode === "login" ? "current-password" : "new-password"} value={f.password} onChange={(e) => setF({ ...f, password: e.target.value })} data-testid="portal-password" /></Field>
          {mode === "register" && (
            <>
              <ProvLoc meta={meta} provincia={f.provincia} localidad={f.localidad} onChange={(p) => setF({ ...f, ...p })} testid="portal" />
              <Field label="WhatsApp / teléfono" className="sm:col-span-2"><Input value={f.telefono} onChange={(e) => setF({ ...f, telefono: e.target.value })} data-testid="portal-telefono" /></Field>
            </>
          )}
          <Btn type="submit" variant="gold" busy={busy} className="sm:col-span-2 !py-3" data-testid="portal-submit">{mode === "login" ? "Ingresar" : "Enviar solicitud"}</Btn>
          {mode === "login" && <button type="button" onClick={() => setMode("forgot")} className="sm:col-span-2 text-center text-sm text-[#0A2463] underline" data-testid="portal-olvide">Olvidé mi contraseña</button>}
          <Link to="/" className="sm:col-span-2 text-center text-xs text-gray-500 underline">Volver a frenteya.com.ar</Link>
        </form>
        </>
        )}
      </div>
    </div>
  );
};

const Instrucciones = ({ client }) => {
  const [txt, setTxt] = useState(null);
  useEffect(() => { client.get("/config").then((r) => setTxt(r.data.instrucciones)).catch(() => setTxt("")); }, [client]);
  if (txt === null) return <Spinner />;
  return <div className="bg-white border border-gray-200 p-6 whitespace-pre-line leading-relaxed text-[#374151]" data-testid="portal-instrucciones">{txt}</div>;
};

const Notificaciones = ({ client, items, onRead }) => {
  useEffect(() => { client.post("/notificaciones/leer").then(onRead).catch(() => {}); }, [client]); // eslint-disable-line react-hooks/exhaustive-deps
  if (!items.length) return <div className="bg-white border p-8 text-center text-sm text-gray-500">No tenés notificaciones.</div>;
  return (
    <div className="bg-white border divide-y" data-testid="portal-notificaciones">
      {items.map((n) => (
        <div key={n.id} className="p-4"><div className="text-xs text-gray-500">{new Date(n.fecha).toLocaleString("es-AR")}</div><div className="font-semibold text-[#0A192F]">{n.titulo}</div><p className="text-sm text-[#374151]">{n.texto}</p></div>
      ))}
    </div>
  );
};

export default function Colaboradores() {
  const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY));
  const [user, setUser] = useState(null);
  const [tab, setTab] = useState("biblioteca");
  const [notis, setNotis] = useState([]);
  const [refresh, setRefresh] = useState(0);
  const [meta] = useMeta();
  const client = useMemo(() => makeClient({ token }), [token]);

  const logout = () => {
    localStorage.removeItem(TOKEN_KEY);
    setToken(null);
    setUser(null);
  };

  useEffect(() => {
    if (!token) return;
    client.get("/auth/me").then((r) => setUser(r.data)).catch(logout);
    client.get("/notificaciones").then((r) => setNotis(r.data)).catch(() => {});
  }, [client, token]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!token || window.location.hash.includes("reset=")) return <AuthScreen meta={meta} onLogin={(d) => { localStorage.setItem(TOKEN_KEY, d.token); setUser(d.user); setToken(d.token); }} />;
  if (!user) return <div className="min-h-screen flex items-center justify-center"><Spinner /></div>;

  const unread = notis.filter((n) => !n.leido).length;
  const TABS = [
    ["biblioteca", "Biblioteca", Library],
    ["subir", "Subir material", Upload],
    ["envios", "Mis envíos", ListChecks],
    ["instrucciones", "Instrucciones", BookOpen],
    ["notificaciones", `Avisos${unread ? ` (${unread})` : ""}`, Bell],
    ...(user.rol === "moderador" ? [["moderacion", "Moderación", ShieldCheck]] : []),
  ];

  return (
    <div className="min-h-screen bg-[#F4F5F7]" data-testid="portal">
      <header className="bg-[#0A192F] text-white">
        <div className="max-w-6xl mx-auto px-4 py-4 flex items-center gap-4">
          <Link to="/"><LogoMark className="h-8" variant="light" /></Link>
          <div className="flex-1 min-w-0">
            <div className="font-headings uppercase tracking-[0.2em] text-[10px] text-[#F5B400]">Red de contenidos</div>
            <div className="text-sm truncate" data-testid="portal-user">{user.nombre} · <span className="text-white/60">{user.rol === "moderador" ? "Moderador/a" : "Colaborador/a"}</span></div>
          </div>
          <button onClick={logout} className="text-white/70 hover:text-white inline-flex items-center gap-1 text-sm" data-testid="portal-logout"><LogOut size={16} /> Salir</button>
        </div>
        <nav className="max-w-6xl mx-auto px-2 flex overflow-x-auto">
          {TABS.map(([id, l, Icon]) => (
            <button key={id} onClick={() => setTab(id)} className={`px-4 py-3 text-sm whitespace-nowrap inline-flex items-center gap-2 border-b-2 ${tab === id ? "border-[#F5B400] text-white" : "border-transparent text-white/60 hover:text-white"}`} data-testid={`portal-nav-${id}`}>
              <Icon size={15} /> {l}
            </button>
          ))}
        </nav>
      </header>
      <main className="max-w-6xl mx-auto px-4 py-6">
        {tab === "biblioteca" && <Biblioteca client={client} meta={meta} />}
        {tab === "subir" && <EnvioForm client={client} meta={meta} user={user} onSent={() => setRefresh((x) => x + 1)} />}
        {tab === "envios" && <MisEnvios client={client} refreshKey={refresh} />}
        {tab === "instrucciones" && <Instrucciones client={client} />}
        {tab === "notificaciones" && <Notificaciones client={client} items={notis} onRead={() => setNotis((xs) => xs.map((n) => ({ ...n, leido: true })))} />}
        {tab === "moderacion" && user.rol === "moderador" && <EnviosList client={client} meta={meta} defaultEstado="pendiente" testid="portal-moderacion" />}
      </main>
    </div>
  );
}
